import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaEnvelope, FaArrowLeft } from 'react-icons/fa';
import { authService } from '../../services/ParseService';

import { useTranslation } from 'react-i18next';

// Import TokLive logo
import tokLiveLogo from '../../assets/icons/ic_logo_dark.png';

const ForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a2a6c, #b21f1f, #fdbb2d);
  padding: 2rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  padding: 3rem;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 2rem;
  color: #1a2a6c;
  
  img {
    height: 70px;
    margin-right: 10px;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
  color: #333;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
  color: #666;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const StyledField = styled(Field)`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    border-color: #1a2a6c;
    outline: none;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 1rem;
  color: #999;
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  margin-left: 0.5rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background-color: #1a2a6c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #152354;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const BackToLogin = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
  color: #666;
  text-decoration: none;
  
  &:hover {
    color: #1a2a6c;
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

const ForgotPasswordPage = () => {
  const [resetSent, setResetSent] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await authService.requestPasswordReset(values.email);
      setResetSent(true);
      toast.success(t('auth.forgotPassword.resetSent'));
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(t('auth.forgotPassword.resetError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ForgotPasswordContainer>
      <Card>
        <Logo>
          <img src={tokLiveLogo} alt="TokLive Logo" />
        </Logo>
        
        {resetSent ? (
          <>
            <SuccessMessage>
              <h3 style={{ marginBottom: '1rem' }}>{t('auth.forgotPassword.resetSent')}</h3>
              <p>
                {t('auth.forgotPassword.resetSentMessage')}
              </p>
            </SuccessMessage>
            
            <BackToLogin to="/login">
              <FaArrowLeft /> {t('auth.forgotPassword.backToLogin')}
            </BackToLogin>
          </>
        ) : (
          <>
            <Title>{t('auth.forgotPassword.title')}</Title>
            <Subtitle>
              {t('auth.forgotPassword.subtitle')}
            </Subtitle>
            
            <Formik
              initialValues={{ email: '' }}
              validationSchema={ForgotPasswordSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  <FormGroup>
                    <InputIcon>
                      <FaEnvelope />
                    </InputIcon>
                    <StyledField
                      type="email"
                      name="email"
                      placeholder={t('auth.forgotPassword.email')}
                    />
                    <ErrorMessage name="email" component={ErrorText} />
                  </FormGroup>
                  
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.resetButton')}
                  </Button>
                </Form>
              )}
            </Formik>
            
            <BackToLogin to="/login">
              <FaArrowLeft /> {t('auth.forgotPassword.backToLogin')}
            </BackToLogin>
          </>
        )}
      </Card>
    </ForgotPasswordContainer>
  );
};

export default ForgotPasswordPage;
